import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip,
  TextField, MenuItem, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  IconButton, Tooltip, Divider, TablePagination,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ReceiptLong as ReceiptIcon, Search as SearchIcon, Refresh as RefreshIcon,
  RestartAlt as ResetIcon, AttachMoney as MoneyIcon, Group as CashierIcon,
  ShowChart as ChartIcon, FilterList as FilterIcon, Visibility as ViewIcon,
  GetApp as DownloadIcon, TrendingUp as TrendIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import ReceiptDialog from './ReceiptDialog';
import OtherReceiptDialog from './OtherReceiptDialog';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const today    = () => new Date().toISOString().slice(0, 10);
// Default the Receipts overview to TODAY only — the cashier almost always
// wants to see what they've taken in today first, and pulling years of
// history on every open would slow the page down. The user can widen the
// range from the From / To date pickers when they need historical data.
const monthAgo = () => today();

// ── Stat tile ──────────────────────────────────────────────────
function StatTile({ icon, label, value, sub, tone = 'primary' }) {
  return (
    <Card sx={{ flex: 1, minWidth: 200 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          bgcolor: (t) => alpha(t.palette[tone].main, 0.12),
          color: `${tone}.main`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, fontSize: 10 }}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{ ...TNUM, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {sub}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Daily trend LINE chart (inline SVG, no dep) ───────────────
// Three series: Fee Payments (blue), Other / Class (purple), Total (green
// dashed). Each point is a small circle with a hover-title showing the
// breakdown for that day.
function DailyTrendChart({ data }) {
  const theme = useTheme();
  const padX = 40, padTop = 16, padBottom = 30;
  const width = 760, height = 240;
  const innerW = width - padX * 2;
  const innerH = height - padTop - padBottom;

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
        <ChartIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body2">No payments in the selected range.</Typography>
      </Box>
    );
  }

  // Series colours
  const C_FEE   = theme.palette.primary.main;     // blue
  const C_OTHER = theme.palette.secondary.main;   // purple
  const C_TOTAL = theme.palette.success.main;     // green

  // Y scale — pick the larger of (fee, other, total) so all three lines fit
  const maxVal = Math.max(
    1,
    ...data.map(d => Number(d.fee_total)),
    ...data.map(d => Number(d.other_total)),
    ...data.map(d => Number(d.fee_total) + Number(d.other_total))
  );
  const niceMax = Math.ceil(maxVal / 100) * 100;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(t => niceMax * t);

  // X scale — equal spacing between days; single point gets centred
  const xAt = (i) =>
    data.length === 1
      ? padX + innerW / 2
      : padX + (i / (data.length - 1)) * innerW;
  const yAt = (v) => padTop + innerH - (Number(v || 0) / niceMax) * innerH;

  const buildPath = (key) =>
    data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(d[key])}`).join(' ');

  const feePath   = buildPath('fee_total');
  const otherPath = buildPath('other_total');
  const totalPath = data.map((d, i) =>
    `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(Number(d.fee_total) + Number(d.other_total))}`
  ).join(' ');

  // Soft area under the totals line for visual weight
  const areaPath =
    `${totalPath} L ${xAt(data.length - 1)} ${yAt(0)} L ${xAt(0)} ${yAt(0)} Z`;

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height} style={{ display: 'block' }}>
        <defs>
          <linearGradient id="totalArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor={C_TOTAL} stopOpacity="0.18" />
            <stop offset="100%" stopColor={C_TOTAL} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Y grid + tick labels */}
        {yTicks.map((v, i) => {
          const y = padTop + innerH - (v / niceMax) * innerH;
          return (
            <g key={i}>
              <line x1={padX} y1={y} x2={padX + innerW} y2={y}
                stroke={alpha(theme.palette.divider, 0.7)} strokeDasharray={i ? '2 3' : '0'} strokeWidth="1" />
              <text x={padX - 6} y={y + 3} fontSize="9" textAnchor="end"
                fill={theme.palette.text.secondary}>
                {v.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </text>
            </g>
          );
        })}

        {/* Filled area under the Total line */}
        <path d={areaPath} fill="url(#totalArea)" />

        {/* Series lines — Total first (so it sits behind the others) */}
        <path d={totalPath}  fill="none" stroke={C_TOTAL} strokeWidth="2"
          strokeDasharray="6 4" strokeLinejoin="round" strokeLinecap="round" />
        <path d={feePath}    fill="none" stroke={C_FEE}   strokeWidth="2.4"
          strokeLinejoin="round" strokeLinecap="round" />
        <path d={otherPath}  fill="none" stroke={C_OTHER} strokeWidth="2.4"
          strokeLinejoin="round" strokeLinecap="round" />

        {/* Data points + X labels */}
        {data.map((d, i) => {
          const fee = Number(d.fee_total);
          const oth = Number(d.other_total);
          const tot = fee + oth;
          const x = xAt(i);
          const showLabel = data.length <= 14 || i % Math.ceil(data.length / 14) === 0;
          const dt = new Date(d.pay_date);
          const labelText = isNaN(dt.getTime())
            ? String(d.pay_date)
            : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
          return (
            <g key={d.pay_date}>
              <title>{`${fmtDate(d.pay_date)}\nFee:   GHS ${fmtMoney(fee)}\nOther: GHS ${fmtMoney(oth)}\nTotal: GHS ${fmtMoney(tot)}`}</title>
              {/* invisible vertical hover hit-target */}
              <line x1={x} y1={padTop} x2={x} y2={padTop + innerH}
                stroke="transparent" strokeWidth={Math.max(8, innerW / data.length / 2)} />
              <circle cx={x} cy={yAt(fee)} r="3.2" fill={C_FEE}   stroke="#fff" strokeWidth="1" />
              <circle cx={x} cy={yAt(oth)} r="3.2" fill={C_OTHER} stroke="#fff" strokeWidth="1" />
              <circle cx={x} cy={yAt(tot)} r="2.6" fill={C_TOTAL} stroke="#fff" strokeWidth="1" />
              {showLabel && (
                <text x={x} y={padTop + innerH + 16} fontSize="9.5"
                  textAnchor="middle" fill={theme.palette.text.secondary}>
                  {labelText}
                </text>
              )}
            </g>
          );
        })}

        <line x1={padX} y1={padTop + innerH} x2={padX + innerW} y2={padTop + innerH}
          stroke={theme.palette.text.secondary} strokeWidth="1" />
      </svg>

      {/* Legend */}
      <Stack direction="row" spacing={2.5} justifyContent="center" sx={{ mt: 0.6 }} flexWrap="wrap">
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Box sx={{ width: 18, height: 3, bgcolor: C_FEE, borderRadius: 1 }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Fee Payments</Typography>
        </Stack>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Box sx={{ width: 18, height: 3, bgcolor: C_OTHER, borderRadius: 1 }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Other / Class</Typography>
        </Stack>
        <Stack direction="row" spacing={0.6} alignItems="center">
          <Box sx={{
            width: 18, height: 0,
            borderTop: `2px dashed ${C_TOTAL}`,
          }} />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Total</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

// ── Horizontal bars: top cashiers ─────────────────────────────
// Rotates through a fixed colour palette so each cashier visually stands
// apart at a glance. The order matches the sort (highest earner first).
const CASHIER_PALETTE = [
  '#1565C0', // primary blue
  '#7B1FA2', // purple
  '#2E7D32', // green
  '#EF6C00', // orange
  '#0097A7', // teal
  '#C62828', // red
  '#5D4037', // brown
  '#455A64', // blue-grey
  '#AD1457', // pink
  '#558B2F', // lime green
];
function CashierBreakdown({ rows }) {
  const theme = useTheme();
  if (!rows || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
        No cashier activity in this range.
      </Typography>
    );
  }
  const max = Math.max(1, ...rows.map(r => Number(r.total)));
  return (
    <Stack spacing={1.2}>
      {rows.slice(0, 10).map((r, i) => {
        const pct = (Number(r.total) / max) * 100;
        const colour = CASHIER_PALETTE[i % CASHIER_PALETTE.length];
        return (
          <Box key={r.name || i}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.4 }}>
              <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{
                  width: 10, height: 10, borderRadius: '50%', bgcolor: colour,
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{ fontWeight: 700 }} noWrap>
                  {r.name || '—'}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={TNUM}>
                GHS {fmtMoney(r.total)} · {r.count} receipt{r.count === 1 ? '' : 's'}
              </Typography>
            </Stack>
            <Box sx={{
              height: 9, borderRadius: 1,
              bgcolor: alpha(theme.palette.divider, 0.4),
              overflow: 'hidden',
            }}>
              <Box sx={{
                width: `${pct}%`, height: '100%',
                background: `linear-gradient(90deg, ${colour}, ${alpha(colour, 0.65)})`,
                transition: 'width 0.3s',
              }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

// ── Top-level Receipts tab ─────────────────────────────────────
export default function ReceiptsTab() {
  const [filters, setFilters] = useState({
    from:    monthAgo(),
    to:      today(),
    scope:   '',     // '' (all) | 'FEE' | 'OTHER'
    mode:    '',
    cashier: '',
  });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(0);
  const [perPage, setPP]      = useState(25);

  const [cashiers, setCashiers] = useState([]);

  // Receipt dialog state — separate for FEE vs OTHER
  const [feeOpen, setFeeOpen]       = useState(false);
  const [feeData, setFeeData]       = useState(null);
  const [feeLoad, setFeeLoad]       = useState(false);
  const [feeError, setFeeError]     = useState('');
  const [otherOpen, setOtherOpen]   = useState(false);
  const [otherData, setOtherData]   = useState(null);
  const [otherLoad, setOtherLoad]   = useState(false);
  const [otherError, setOtherError] = useState('');

  // Load cashiers once
  useEffect(() => {
    API.get('/payments/cashiers').then(r => setCashiers(r.data || [])).catch(() => {});
  }, []);

  const load = async (next = filters) => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (next.from)    params.from = next.from;
      if (next.to)      params.to = next.to;
      if (next.scope)   params.scope = next.scope;
      if (next.mode)    params.mode = next.mode;
      if (next.cashier) params.cashier = next.cashier;
      const r = await API.get('/payments/overview', { params });
      setData(r.data);
      setPage(0);
    } catch (e) {
      setError(swal.pickError(e, 'Could not load receipts'));
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setF = (k, v) => setFilters(p => ({ ...p, [k]: v }));
  const apply = () => load();
  const reset = () => {
    const n = { from: monthAgo(), to: today(), scope: '', mode: '', cashier: '' };
    setFilters(n); load(n);
  };

  // Client-side search across the loaded receipts
  const receipts = data?.receipts || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return receipts;
    return receipts.filter(r =>
      (r.receipt_no || '').toLowerCase().includes(q) ||
      (r.payer_name || '').toLowerCase().includes(q) ||
      (r.student_id || '').toLowerCase().includes(q) ||
      (r.payment_type || '').toLowerCase().includes(q) ||
      (r.received_by_name || '').toLowerCase().includes(q)
    );
  }, [receipts, search]);
  const paged = filtered.slice(page * perPage, page * perPage + perPage);

  // Open the right receipt dialog. Legacy rows (entered via the old Java
  // app) don't have a payment_id / transaction_id — show a friendly note
  // instead of crashing the dialog.
  const openReceipt = async (r) => {
    if (r.payment_scope === 'FEE' && r.payment_id) {
      setFeeOpen(true); setFeeLoad(true); setFeeData(null); setFeeError('');
      try {
        const det = await API.get(`/payments/${r.payment_id}/receipt`);
        setFeeData(det.data);
      } catch (e) { setFeeError(swal.pickError(e, 'Could not load receipt')); }
      finally { setFeeLoad(false); }
      return;
    }
    if (r.payment_scope !== 'FEE' && r.transaction_id) {
      setOtherOpen(true); setOtherLoad(true); setOtherData(null); setOtherError('');
      try {
        const det = await API.get(`/other-payments/${r.transaction_id}/receipt`);
        setOtherData(det.data);
      } catch (e) { setOtherError(swal.pickError(e, 'Could not load receipt')); }
      finally { setOtherLoad(false); }
      return;
    }
    // Legacy row — no rich receipt data available.
    swal.info(
      `Receipt ${r.receipt_no || ''}\n${r.payer_name || ''} · ${r.payment_type || ''} · GHS ${fmtMoney(r.total_amount)}\nRecorded by ${r.received_by_name || '—'} on ${fmtDate(r.pay_date)}.\n\nThis receipt was created in the legacy system — printable detail isn't available here.`,
      'Legacy receipt'
    );
  };

  const exportCSV = () => {
    if (filtered.length === 0) return;
    const header = ['Date', 'Time', 'Receipt', 'Scope', 'Type', 'Student/Payer', 'Class', 'Amount', 'Mode', 'Bank', 'Cashier', 'Status'];
    const rows = filtered.map(r => ([
      r.pay_date,
      fmtTime(r.created_at),
      r.receipt_no,
      r.payment_scope,
      r.payment_type || '—',
      r.payer_name || r.student_id || '—',
      r.class_name || '—',
      r.total_amount,
      r.mode,
      r.bank_account || '',
      r.received_by_name || '',
      r.status || '',
    ]));
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Receipts-${filters.from}_to_${filters.to}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const stats = data?.stats || { count: 0, total: 0, avg: 0, cashier_count: 0 };

  return (
    <Box sx={{ fontFamily: FONT_ACCT }}>
      {/* Filter bar */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2 }}>
            <FilterIcon color="primary" />
            <Typography fontWeight={800}>Receipts Overview</Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh"><span>
              <IconButton size="small" onClick={() => load()} disabled={loading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Reset filters">
              <IconButton size="small" onClick={reset}><ResetIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' },
            gap: 1,
          }}>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              value={filters.from} onChange={(e) => setF('from', e.target.value)} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              value={filters.to} onChange={(e) => setF('to', e.target.value)} />
            <TextField select size="small" label="Scope" value={filters.scope}
              onChange={(e) => setF('scope', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="FEE">Fee Payment</MenuItem>
              <MenuItem value="OTHER">Other / Class</MenuItem>
            </TextField>
            <TextField select size="small" label="Mode" value={filters.mode}
              onChange={(e) => setF('mode', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['Cash', 'Bank Transfer', 'Mobile Money', 'Cheque', 'POS'].map(m =>
                <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Cashier" value={filters.cashier}
              onChange={(e) => setF('cashier', e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {cashiers.map(c => <MenuItem key={c.name} value={c.name}>{c.name}</MenuItem>)}
            </TextField>
            <Button variant="contained" onClick={apply} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Apply
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stat tiles */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <StatTile icon={<ReceiptIcon />} label="Total Receipts"
          value={Number(stats.count).toLocaleString()} tone="primary"
          sub={`${filters.from} → ${filters.to}`} />
        <StatTile icon={<MoneyIcon />} label="Total Amount"
          value={`GHS ${fmtMoney(stats.total)}`} tone="success" />
        <StatTile icon={<TrendIcon />} label="Avg per Receipt"
          value={`GHS ${fmtMoney(stats.avg)}`} tone="secondary" />
        <StatTile icon={<CashierIcon />} label="Active Cashiers"
          value={stats.cashier_count} tone="warning" />
      </Stack>

      {/* Charts row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <ChartIcon color="primary" />
              <Typography fontWeight={800}>Daily Receipts</Typography>
              <Box sx={{ flex: 1 }} />
              {data?.daily?.length > 0 && (
                <Chip size="small" sx={{ fontWeight: 700 }}
                  label={`${data.daily.length} day(s)`} />
              )}
            </Stack>
            <DailyTrendChart data={data?.daily} />
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <CashierIcon color="primary" />
              <Typography fontWeight={800}>Top Cashiers</Typography>
            </Stack>
            <CashierBreakdown rows={data?.by_cashier} />
            {data?.by_mode?.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9.5, display: 'block', mb: 0.6 }}>
                  By Payment Mode
                </Typography>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" rowGap={0.6}>
                  {data.by_mode.map(m => (
                    <Chip key={m.mode} size="small" variant="outlined"
                      label={`${m.mode} · ${m.count} · GHS ${fmtMoney(m.total)}`}
                      sx={{ fontWeight: 700, ...TNUM }} />
                  ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Receipts table */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
            <ReceiptIcon color="primary" />
            <Typography fontWeight={800}>Transaction History</Typography>
            <Box sx={{ flex: 1 }} />
            <TextField size="small" placeholder="Search receipts…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 240 }}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }} />
            <Tooltip title="Export visible rows as CSV">
              <span>
                <Button size="small" startIcon={<DownloadIcon />} variant="outlined"
                  onClick={exportCSV} disabled={filtered.length === 0}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Export
                </Button>
              </span>
            </Tooltip>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, py: 0.7, whiteSpace: 'nowrap' } }}>
                  <TableCell>Date · Time</TableCell>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Student / Payer</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Cashier</TableCell>
                  <TableCell align="right" sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={22} />
                  </TableCell></TableRow>
                )}
                {!loading && filtered.length === 0 && (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No receipts in this range.
                  </TableCell></TableRow>
                )}
                {!loading && paged.map(r => {
                  const isFee = r.payment_scope === 'FEE';
                  const isReversed = r.status === 'REVERSED';
                  return (
                    <TableRow key={`day-${r.day_id}`} hover
                      sx={{
                        '& td': { fontSize: 12, py: 0.7, ...TNUM, whiteSpace: 'nowrap' },
                        opacity: isReversed ? 0.6 : 1,
                      }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }}>
                          {fmtDate(r.pay_date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtTime(r.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{r.receipt_no}</TableCell>
                      <TableCell>
                        <Chip size="small" label={isFee ? 'Fee' : 'Other'}
                          color={isFee ? 'primary' : 'secondary'}
                          variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
                      </TableCell>
                      <TableCell>{r.payment_type || '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12 }} noWrap>
                          {r.payer_name || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={TNUM}>
                          {r.student_id || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{r.class_name || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: 'success.dark' }}>
                        GHS {fmtMoney(r.total_amount)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={r.mode} variant="outlined"
                          sx={{ fontWeight: 600, height: 20 }} />
                      </TableCell>
                      <TableCell>{r.received_by_name || '—'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View receipt">
                          <IconButton size="small" color="primary" onClick={() => openReceipt(r)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={filtered.length}
            page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => { setPP(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows:" showFirstButton showLastButton />
        </CardContent>
      </Card>

      <ReceiptDialog
        open={feeOpen} onClose={() => setFeeOpen(false)}
        data={feeData} loading={feeLoad} error={feeError} />
      <OtherReceiptDialog
        open={otherOpen} onClose={() => setOtherOpen(false)}
        data={otherData} loading={otherLoad} error={otherError} />
    </Box>
  );
}
